import React from "react";
import {
  makeStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import styled from "styled-components";

import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import labels from "../../../helpers/labels.json";

import { Formik, FieldArray } from "formik";
import { PrimaryCTAButton, SecondaryCTAButton } from "../../common/Buttons";
import CaseService from "../../../services/CaseService";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import { getErrorMessage } from "../../../helpers/functions";
import CustomSelect from "../../common/CustomSelect/CustomSelect";
import { CustomInputField } from "../../common/FormInputs";
import useUser from "../../../hooks/useUser";

const useStyles = makeStyles((theme) => ({
  accordion: {
    borderRadius: 12,
    margin: "16px 0 !important",
    border: "1px solid #e0e7ff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    overflow: "hidden",
    transition: "all 0.22s ease",
    backgroundColor: "#ffffff",
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "20px 0 !important",
      boxShadow: "0 6px 24px rgba(0, 131, 140, 0.12)",
      borderColor: "#00838c",
    },
  },
  expanded: {}, // required for $expanded syntax
  summary: {
    minHeight: 64,
    padding: "0 24px",
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    borderBottom: "1px solid #e2e8f0",
    "& .MuiAccordionSummary-content": {
      margin: "12px 0",
      alignItems: "center",
    },
  },
  heading: {
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "1.05rem",
    color: "#293461",
    flexGrow: 1,
  },
  tableHeadCell: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: COLORS.INPUT_LABEL || "#64748b",
    fontSize: "0.81rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    padding: "16px 24px",
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
  },
  tableRow: {
    transition: "background-color 0.18s ease",
    "&:hover": {
      backgroundColor: "#f1f5f9",
      "& $deleteButton": {
        opacity: 1,
      },
    },
  },
  tableCell: {
    padding: "16px 24px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
  },
  lastRowCell: {
    borderBottom: "none !important",
  },
  deleteButton: {
    opacity: 0.4,
    transition: "opacity 0.2s",
    color: "#ef4444",
    "&:hover": {
      backgroundColor: "rgba(239, 68, 68, 0.08)",
    },
  },
  inputRoot: {
    backgroundColor: "#ffffff",
    borderRadius: 8,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#00838c",
      borderWidth: 1.5,
    },
  },
}));

function BulkUpload({ id, setTriggerCaseCall, title, caseDetails }) {
  const classes = useStyles();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const { authSetting } = useUser();

  const initialRow = { extraField: "", value: "" };

  const initialValues = {
    extra_fields_items:
      caseDetails?.arbitrationAgreementFields &&
      Object.keys(caseDetails.arbitrationAgreementFields).length > 0
        ? Object.entries(caseDetails.arbitrationAgreementFields).map(
            ([k, v]) => ({
              extraField: k,
              value: v || "",
            }),
          )
        : [initialRow],
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoader({ state: true, message: "Updating fields..." });

      const arbitrationAgreementFields = values.extra_fields_items.reduce(
        (acc, row) => {
          if (row.extraField?.trim()) {
            acc[row.extraField.trim()] = row.value?.trim() || "";
          }
          return acc;
        },
        {},
      );

      const res = await CaseService.arbAgreementDetails(id, {
        arbitrationAgreementFields,
      });

      enqueueSnackbar(res?.message || "Saved successfully", {
        variant: "success",
      });
      setTriggerCaseCall(true);
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: "error" });
    } finally {
      setLoader({ state: false });
      setSubmitting(false);
    }
  };

  return (
    <Accordion className={classes.accordion} defaultExpanded={false}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon style={{ color: "#00838c" }} />}
        className={classes.summary}
      >
        <div className={classes.heading}>
          {title || "Arbitration Agreement Fields"}
        </div>
      </AccordionSummary>

      <AccordionDetails style={{ padding: 0, background: "#ffffff" }}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            handleChange,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <div style={{ padding: "24px 24px 16px" }}>
                <TableContainerStyled>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeadCell}>
                          Extra Field
                        </TableCell>
                        <TableCell className={classes.tableHeadCell}>
                          Value
                        </TableCell>
                        <TableCell
                          className={classes.tableHeadCell}
                          align="center"
                          width={80}
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <FieldArray name="extra_fields_items">
                      {({ remove, push }) => (
                        <TableBody>
                          {values.extra_fields_items.map((row, idx) => (
                            <TableRow
                              key={idx}
                              className={classes.tableRow}
                              hover
                            >
                              <TableCell className={classes.tableCell}>
                                <CustomSelect
                                  menuItemValues={
                                    authSetting?.noticeGenerationFields || []
                                  }
                                  value={row.extraField || ""}
                                  name={`extra_fields_items.${idx}.extraField`}
                                  onChange={handleChange}
                                  variant="outlined"
                                  fullWidth
                                  margin="dense"
                                  InputProps={{
                                    classes: { root: classes.inputRoot },
                                  }}
                                />
                              </TableCell>

                              <TableCell className={classes.tableCell}>
                                <CustomInputField
                                  value={row.value || ""}
                                  name={`extra_fields_items.${idx}.value`}
                                  onChange={handleChange}
                                  variant="outlined"
                                  fullWidth
                                  margin="dense"
                                  InputProps={{
                                    classes: { root: classes.inputRoot },
                                  }}
                                />
                              </TableCell>

                              <TableCell
                                align="center"
                                className={classes.tableCell}
                              >
                                {values.extra_fields_items.length > 1 && (
                                  <IconButton
                                    size="small"
                                    className={classes.deleteButton}
                                    onClick={() => remove(idx)}
                                    aria-label="remove field"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      )}
                    </FieldArray>
                  </Table>
                </TableContainerStyled>

                <ButtonArea>
                  <SecondaryCTAButton
                    startIcon={<AddCircleOutlineIcon fontSize="small" />}
                    onClick={() => {
                      setFieldValue("extra_fields_items", [
                        ...values.extra_fields_items,
                        initialRow,
                      ]);
                    }}
                    style={{ textTransform: "none" }}
                  >
                    Add Extra Field
                  </SecondaryCTAButton>

                  <PrimaryCTAButton
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      minWidth: 140,
                      padding: "8px 24px",
                      fontWeight: 600,
                    }}
                  >
                    {isSubmitting ? "Saving…" : labels["buttons.save"]}
                  </PrimaryCTAButton>
                </ButtonArea>
              </div>
            </form>
          )}
        </Formik>
      </AccordionDetails>
    </Accordion>
  );
}

export default BulkUpload;

const TableContainerStyled = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: white;
`;

const ButtonArea = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 600px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

export const AccordionHeading = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
`;

export const TableContainer = styled.div`
  width: 100%;
`;
